/*
  Component: Country Selector — filtering and submit feedback
  Responsibility: reveal the search field, narrow the country list to what was typed, and —
                  once a market is chosen — shut the panel and show that something is
                  happening while the page reloads behind it. Opening the panel is <details>,
                  dismissing it is assets/kl-disclosure.js, and switching market is the form
                  post the snippet already renders, which nothing here intercepts.

  The field renders hidden and this file reveals it by adding .is-enhanced. That order is
  deliberate and matches the footer's accordion: if this script never loads, the shopper
  gets the full list and no search box, rather than a search box that does nothing.

  What a row matches on is the row's own wording, currency and all — "India (INR ₹)", not
  "India". The rule is that anything readable in a row is searchable, so typing INR finds
  India and typing USD finds every market priced in dollars, which is what a shopper looking
  for a currency rather than a country actually types.

  Nothing here reads the text of a row to get it. snippets/kl-country-selector.liquid
  captures that wording once and puts it in data-kl-country-selector-item, which is both the
  hook and the value, so the markup can be restyled without the filter noticing — and the
  two cannot drift, which is the part that keeps the rule above true.

  The filter is cleared whenever the panel shuts, so it always reopens showing every market.
  <details> fires `toggle`, which is the only event needed for that — this file never opens
  or closes anything itself.
*/

/*
  Pure, and the only real logic in the file: accents are folded so that typing "aland"
  finds "Åland Islands", and case is dropped on both sides. An empty query matches
  everything, which is what makes clearing the field restore the list.
*/
function normalizeSearchText(value) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

function isRowMatch(rowText, normalizedQuery) {
  if (!normalizedQuery) return true;

  return normalizeSearchText(rowText).indexOf(normalizedQuery) > -1;
}

function initCountrySelector(selector) {
  const disclosure = selector.querySelector('[data-kl-country-selector-disclosure]');
  const searchInput = selector.querySelector('[data-kl-country-selector-search]');
  const items = Array.from(selector.querySelectorAll('[data-kl-country-selector-item]'));
  const options = Array.from(selector.querySelectorAll('[data-kl-country-selector-option]'));

  if (!disclosure || !searchInput || !items.length || !options.length) return;

  function applyFilter() {
    try {
      const normalizedQuery = normalizeSearchText(searchInput.value);

      items.forEach((item) => {
        const rowText = item.dataset.klCountrySelectorItem || '';

        item.classList.toggle('is-hidden', !isRowMatch(rowText, normalizedQuery));
      });
    } catch (error) {
      console.debug('kl-country-selector: could not filter the country list', { error, query: searchInput.value });
    }
  }

  /*
    The rows are the form's submit buttons, so Enter in the search field would trigger
    implicit submission and switch the shopper to whichever market happens to be first in
    the list. There is nothing to submit from here — choosing a market is a click on a row —
    so Enter is swallowed outright.
  */
  function preventImplicitSubmit(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
  }

  /*
    Shut the panel and put the spinner up beside the pill. Feedback and nothing else — the
    click is left to submit the form exactly as it would have, so a market still switches
    even if this throws.

    Shutting the disclosure does not cancel the submission it was clicked to start. The
    button stays in the document; a collapsed <details> only stops it being rendered, and a
    submit button's activation behaviour runs after the click has finished dispatching.
  */
  function showPendingState() {
    try {
      disclosure.removeAttribute('open');
      selector.classList.add('is-submitting');
    } catch (error) {
      console.debug('kl-country-selector: could not mark the pending market', { error });
    }
  }

  /*
    Both resets hang off `toggle` rather than off the page's lifecycle, and that is a
    deliberate limit rather than the obvious place for them.

    The filter is cleared on the way shut so the panel always reopens showing every market.
    The spinner is cleared on the way open, which matters for one path: the back button
    restores this page from the browser's cache with its DOM exactly as it was left, spinner
    included, on a page that is not loading anything. pageshow is the event for that and it
    fires on window, which CLAUDE.md rules out — so the spinner is cleared at the next
    opening instead, and nothing here disables the pill while submitting precisely so that
    reopening stays available as the way out. A shopper who goes back and does not touch the
    picker sees a ring turning beside it until they do. That is the residue, and it is the
    cheaper end of the trade.
  */
  function handleToggle() {
    if (disclosure.open) {
      selector.classList.remove('is-submitting');
      return;
    }

    searchInput.value = '';
    applyFilter();
  }

  selector.classList.add('is-enhanced');

  searchInput.addEventListener('input', applyFilter);
  searchInput.addEventListener('keydown', preventImplicitSubmit);
  disclosure.addEventListener('toggle', handleToggle);
  options.forEach((option) => option.addEventListener('click', showPendingState));
}

document.querySelectorAll('[data-kl-country-selector]').forEach(initCountrySelector);
